import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  Archive,
  ArrowRight,
  Binary,
  Blocks,
  BookOpen,
  Database,
  ExternalLink,
  FileSearch,
  Github,
  Heart,
  KeyRound,
  Lock,
  SearchCheck,
  ShieldCheck,
  Terminal,
  Waypoints
} from "lucide-react";
import { AuthDialog } from "./components/AuthDialog";
import { Downloads } from "./components/Downloads";
import { Roadmap } from "./components/Roadmap";
import { SafetyNotice } from "./components/SafetyNotice";
import { auth, authReady } from "./lib/firebase";

const repositoryUrl = import.meta.env.VITE_GITHUB_REPOSITORY_URL || "https://github.com/MASHR-SYSTEM/blockr";
const sourceDownloadUrl = "/download#source-code";
const donationBaseUrl = import.meta.env.VITE_DONATION_URL || "https://buy.stripe.com/cNi9AScv4cq4h2052J0Jq00";
const donationUrl = (() => {
  const url = new URL(donationBaseUrl);
  url.searchParams.set("client_reference_id", "blockr");
  url.searchParams.set("utm_source", "blockr");
  url.searchParams.set("utm_medium", "product_site");
  url.searchParams.set("utm_campaign", "collective_support");
  return url.toString();
})();

const capabilities = [
  { icon: Archive, title: "Acquire RAW and E01", text: "Read-only source access, resumable RAW checkpoints, segmented E01, bad-range accounting, and optional destination readback." },
  { icon: Blocks, title: "Inspect Windows disks", text: "MBR/GPT layouts and TSK-backed NTFS, FAT, and exFAT enumeration and extraction." },
  { icon: KeyRound, title: "Stage BitLocker safely", text: "Windows recovery-password workflows operate on a disposable derived VHDX, never by attaching the original image." },
  { icon: FileSearch, title: "Find artifacts and files", text: "Targeted Windows artifact parsing, bounded structural carving, and explicit partial-failure reporting." },
  { icon: SearchCheck, title: "Hunt locally", text: "YARA-X, SHA-256 hash sets, and a bounded STIX indicator subset without uploading evidence." },
  { icon: Database, title: "Keep an auditable case", text: "SQLite/FTS findings, JSONL, exclusive writer locks, a hash-chained audit log, finalization, and verification." }
];

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="Blockr home"><span className="brand-mark" aria-hidden="true"><i /><i /><i /><i /></span><span>blockr</span><small>by mashr.ai</small></a>
      <nav aria-label="Primary navigation">
        <a href="/#safety">Safety</a>
        <a href="/#capabilities">Capabilities</a>
        <a href="/#download">Download</a>
        <a href="/#roadmap">Roadmap</a>
      </nav>
      <a className="header-github" href={sourceDownloadUrl}><Archive size={17} /> Source</a>
    </header>
  );
}

function LegalPage({ kind }: { kind: "privacy" | "terms" }) {
  return (
    <>
      <Header />
      <main id="main-content" className="legal-page">
        <span className="eyebrow">Mashr Systems · Blockr</span>
        <h1>{kind === "privacy" ? "Privacy" : "Terms and experimental-use notice"}</h1>
        <p className="legal-updated">Effective 9 August 2026</p>
        {kind === "privacy" ? (
          <>
            <h2>What the product site does not collect</h2>
            <p>Blockr runs on your computer. This website does not accept disk images, case files, recovered artifacts, credentials, or other forensic evidence. It does not run advertising or product analytics in this release.</p>
            <h2>Mashr accounts and community participation</h2>
            <p>Public roadmap reading and downloads require no account. If you register to vote or suggest, Firebase processes your Mashr identity. The community service stores your Firebase UID, vote records, aggregate counters, suggestion text and reference, and short-lived rate-limit state. It does not copy your account email into the community database.</p>
            <h2>Suggestions</h2>
            <p>Suggestions enter a private moderation queue and may later be promoted into the public roadmap. Never submit case evidence, personal information, credentials, or sensitive incident details.</p>
            <h2>Service providers</h2>
            <p>Firebase provides identity, AWS hosts the site and community API, GitHub hosts the private development repository, and Stripe processes optional donations. Their own privacy terms apply when you use those services.</p>
            <h2>Your choices</h2>
            <p>You can browse anonymously and sign out at any time. Short-lived rate-limit records expire automatically. Votes and suggestions are retained until Mashr adds a separately reviewed self-service deletion and retention policy.</p>
          </>
        ) : (
          <>
            <h2>Experimental software</h2>
            <p>Blockr is pre-release test software supplied without warranty. It makes no forensic-assurance, accreditation, admissibility, completeness, or fitness claim. It is not a hardware or software write blocker.</p>
            <h2>Authorized and careful use</h2>
            <p>Use Blockr only on systems and media you are legally authorized to examine. When preservation matters, use a tested hardware write blocker and an independently documented procedure. You are responsible for validating Blockr against your environment and requirements.</p>
            <h2>Open-source licensing</h2>
            <p>The Blockr application source is offered under MIT OR Apache-2.0. Bundled native and third-party components retain their own licenses and notices. The repository license files control over this summary.</p>
            <h2>Community conduct</h2>
            <p>Roadmap votes are advisory and confer no delivery, support, or priority entitlement. Donations do not buy influence. Suggestions must not contain evidence, secrets, personal information, unlawful material, or abusive content, and may be rejected or removed.</p>
          </>
        )}
        <a className="button secondary" href="/">Return to Blockr</a>
      </main>
      <Footer />
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div><strong>Blockr</strong><p>Free, open-source, experimental disk investigation.</p></div>
      <div><span>Product</span><a href={sourceDownloadUrl}>Source archive</a><a href="/#quick-start">Verification and use</a><a href={repositoryUrl}>Private test repository</a></div>
      <div><span>Legal</span><a href="/terms">Terms</a><a href="/privacy">Privacy</a><a href={sourceDownloadUrl}>MIT / Apache-2.0 source</a></div>
      <div><span>Support</span><a href={donationUrl}>Donate</a><a href="https://mashr.ai">mashr.ai</a></div>
      <p className="copyright">© {new Date().getFullYear()} Mashr Systems. Blockr remains experimental software.</p>
    </footer>
  );
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const path = window.location.pathname.replace(/\/$/, "") || "/";

  useEffect(() => {
    if (!auth) return;
    let unsubscribe: () => void = () => {};
    authReady.then(() => { unsubscribe = onAuthStateChanged(auth!, setUser); });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (path === "/download" || path === "/roadmap") {
      const fragment = window.location.hash.slice(1);
      requestAnimationFrame(() => document.getElementById(fragment || path.slice(1))?.scrollIntoView());
    }
  }, [path]);

  if (path === "/privacy" || path === "/terms") return <LegalPage kind={path.slice(1) as "privacy" | "terms"} />;

  return (
    <>
      <Header />
      <main id="main-content">
        <section className="hero">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-copy">
            <div className="hero-badges"><span>Free &amp; open source</span><span className="danger">Alpha test product</span></div>
            <h1>Examine the disk.<br /><em>Question the result.</em></h1>
            <p className="hero-lead">Blockr is an offline-first command-line toolkit for acquiring and investigating Windows-oriented disk evidence, published by Mashr Systems for controlled testing.</p>
            <div className="hero-actions">
              <a className="button primary large" href="#download"><Terminal size={20} /> Download experimental build</a>
              <a className="button secondary large" href={sourceDownloadUrl}><Archive size={20} /> Download source</a>
            </div>
            <p className="hero-platform"><Binary size={16} /> Windows 11 x64 primary · Linux x64 and macOS ARM64 experimental</p>
          </div>
          <div className="hero-console" aria-label="Example Blockr command output">
            <div className="console-top"><i /><i /><i /><span>blockr — controlled workstation</span></div>
            <pre><code><span>$</span> blockr doctor
<b>status</b> experimental
<b>evidence access</b> read-only requested
<b>native worker</b> ready

<span>$</span> blockr analyze layout disk.E01
<b>source</b> EnCase 6 segmented image
<b>partitions</b> bounded MBR/GPT inspection
<b>warning</b> corroborate every parser result</code></pre>
          </div>
        </section>

        <div className="hero-warning"><SafetyNotice /></div>

        <section id="safety" className="section safety-section" aria-labelledby="safety-title">
          <div className="section-heading split-heading">
            <div><span className="eyebrow">Preservation before convenience</span><h2 id="safety-title">A read-only handle is not a write blocker.</h2></div>
            <p>The host, firmware, controller, mounted filesystem, or another process can still alter evidence. Blockr records what it can establish; it cannot replace an authorized procedure.</p>
          </div>
          <div className="principle-grid">
            <article><ShieldCheck /><h3>Use tested write-blocking</h3><p>Attach original physical media through suitable hardware and unmount dependent volumes where preservation matters.</p></article>
            <article><Lock /><h3>Reduce privilege</h3><p>Elevate only acquisition, VSS, or BitLocker operations. Run analysis of hostile data as an unprivileged user.</p></article>
            <article><SearchCheck /><h3>Verify and corroborate</h3><p>Verify the signed archive, preserve manifests, inspect recorded read errors, and corroborate parser findings independently.</p></article>
          </div>
          <a className="text-link" href="/#quick-start">Review verification and operating guidance before use <ArrowRight size={15} /></a>
        </section>

        <section id="capabilities" className="section capabilities-section" aria-labelledby="capabilities-title">
          <div className="section-heading"><span className="eyebrow">Implemented in v0.1</span><h2 id="capabilities-title">A transparent pipeline from bytes to findings.</h2><p>Every capability has documented boundaries. Unsupported formats and partial failures should be reported, never quietly presented as complete analysis.</p></div>
          <div className="capability-grid">
            {capabilities.map(({ icon: Icon, title, text }) => <article key={title}><Icon /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
          <div className="pipeline" aria-label="Blockr data flow"><span>Device / RAW / E01</span><Waypoints /><span>Acquisition and analysis</span><Waypoints /><span>Normalized findings</span><Waypoints /><span>Audited case and reports</span></div>
        </section>

        <Downloads />

        <section className="section open-source-section" aria-labelledby="open-title">
          <div>
            <span className="eyebrow">Inspect it, test it, improve it</span>
            <h2 id="open-title">Open source is part of the evidence story.</h2>
            <p>Every release includes a public source snapshot dual-licensed under MIT OR Apache-2.0. Native forensic dependencies retain their own licenses. The development repository remains private during the test phase.</p>
            <div className="hero-actions"><a className="button secondary" href={sourceDownloadUrl}><Archive size={18} /> Download source</a><a className="button secondary" href={repositoryUrl}><Github size={18} /> Private test repository</a></div>
          </div>
          <div className="support-card"><Heart aria-hidden="true" /><h3>Keep access free</h3><p>Donations are optional. They never unlock downloads or add voting weight.</p><a className="button primary" href={donationUrl}>Donate to Mashr</a></div>
        </section>

        <Roadmap user={user} onAuthRequest={() => setAuthOpen(true)} onSignOut={() => auth && signOut(auth)} />

        <section className="section docs-cta">
          <BookOpen aria-hidden="true" />
          <div><span className="eyebrow">Do not operate from a landing page</span><h2>Read the limitations and operator guide.</h2></div>
          <a className="button secondary" href="/#download">Get release documentation <ExternalLink size={16} /></a>
        </section>
      </main>
      <Footer />
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}
